/**
 * V3 Event step context — resolves which S1–S5 step a session is currently
 * executing, from the session row's mode_config.v3Config.
 *
 * Closes the P-LIVE-1 gap (CANON_PATCH_BACKLOG): the live/participant surfaces
 * must name the active step, its goal, and its place in the S1–S5 sequence
 * instead of presenting an unframed tool run. Pure function — safe to call from
 * any surface; returns null for non-V3 sessions so callers render nothing
 * (Doc 26 rule 2: absence, not disablement).
 */

import { STEP_META, STEP_ORDER, type StepConfig, type StepId } from './types';

export interface V3StepContext {
  stepId: StepId;
  /** 1-based position in the S1–S5 sequence */
  index: number;
  total: number;
  label: string;
  blurb: string;
  /** The facilitator's stated goal for this step, when set */
  goal?: string;
  /** True when this step is the Event's primary goal ("main event") */
  isMainEvent: boolean;
  /** True when the step is a collaborative step currently running */
  running: boolean;
}

interface V3ConfigShape {
  steps?: Partial<Record<StepId, StepConfig>>;
  primaryGoalStep?: StepId;
}

/**
 * Resolve the active step for a session:
 * 1. a collaborative step with runStatus 'running', else
 * 2. the Event's primaryGoalStep, else
 * 3. null (not a V3 session, or no step signal at all).
 */
export function getV3StepContext(
  modeConfig: unknown,
  activeStepId?: StepId | null,
): V3StepContext | null {
  const v3 = (modeConfig as { v3Config?: V3ConfigShape } | null | undefined)?.v3Config;
  if (!v3?.steps) return null;

  const requestedStep =
    activeStepId && STEP_META[activeStepId] && v3.steps[activeStepId]
      ? activeStepId
      : null;
  const runningStep = STEP_ORDER.find(
    id => v3.steps?.[id]?.mode === 'collaborative' && v3.steps?.[id]?.runStatus === 'running'
  );
  const stepId = requestedStep ?? runningStep ?? v3.primaryGoalStep;
  if (!stepId || !STEP_META[stepId]) return null;

  const config = v3.steps[stepId];
  return {
    stepId,
    index: STEP_ORDER.indexOf(stepId) + 1,
    total: STEP_ORDER.length,
    label: STEP_META[stepId].label,
    blurb: STEP_META[stepId].blurb,
    goal: config?.goal?.trim() || undefined,
    isMainEvent: v3.primaryGoalStep === stepId,
    running: runningStep === stepId || config?.runStatus === 'running',
  };
}
